import { defineStore } from 'pinia';
import {ref} from 'vue';
import DesignationService from '@/services/hrm/designationService';
import { toast } from '@/utils/toast';
import { createScopedCache } from './_cache';
import { MODULE_KEYS } from '@/config/modules-registry';
import { guardPlanModule } from '@/utils/planAccess';

const designationListCache = createScopedCache('designations:list');
const DESIGNATIONS_LIST_TTL = 5 * 60 * 1000;


export const useDesignationStore = defineStore('designation', () => {
  const designations = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function fetchDesignations(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_EMPLOYEE))) {
      designations.value = [];
      return;
    }

    loading.value = true;
    const { force, ...query } = params;
    try {
      await designationListCache.get(query, {
        ttlMs: DESIGNATIONS_LIST_TTL,
        force: force === true,
        loader: async () => {
          const response = await DesignationService.getAll(query);
          return { designations: response.data || [] };
        },
        onHydrate: ({ designations: rows }) => {
          designations.value = rows || [];
        },
      });
    } catch (e) {
      error.value = e.response?.data?.message || 'Failed to fetch designations';
      toast(error.value, 'error');
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function ensureDesignations(params = {}) {
    return await fetchDesignations({ ...params, force: false });
  }

  async function createDesignation(data) {
    loading.value = true;
    try {
      const response = await DesignationService.create(data);
      designationListCache.clear();
      designations.value.push(response.data);
      toast('Designation created successfully!');
      return response.data;
    } catch (e) {
      error.value = e.response?.data?.message || 'Failed to create designation';
      toast(error.value, 'error');
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function updateDesignation(data) {
    loading.value = true;
    try {
      const response = await DesignationService.update(data.id, data);
      designationListCache.clear();
      const idx = designations.value.findIndex(d => d.id === data.id);
      if (idx !== -1) designations.value[idx] = response.data;
      toast('Designation updated successfully!');
      return response.data;
    } catch (e) {
      error.value = e.response?.data?.message || 'Failed to update designation';
      toast(error.value, 'error');
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function deleteDesignations(ids) {
    loading.value = true;
    try {
      await Promise.all(ids.map(id => DesignationService.delete(id)));
      designationListCache.clear();
      designations.value = designations.value.filter(d => !ids.includes(d.id));
      toast('Designations deleted successfully!');
    } catch (e) {
      error.value = e.response?.data?.message || 'Failed to delete designation';
      toast(error.value, 'error');
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function downloadReport(format = 'xlsx', params = {}) {
    try {
      const response = await DesignationService.exportReport(format, params);
      const mime =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([response.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      link.href = url;
      link.setAttribute(
        'download',
        `designation-report-${new Date().toISOString().slice(0, 10)}.${ext}`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to export designations';
      toast(msg, 'error');
      throw e;
    }
  }

  return {
    designations, loading, error,
    fetchDesignations, ensureDesignations, createDesignation, updateDesignation, deleteDesignations, downloadReport,
  };
});
